import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Sun, Moon, KeyRound, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [myListing, setMyListing] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setMyListing(null);
      return;
    }
    let active = true;
    api
      .get("/companies/mine")
      .then((r) => {
        if (active) setMyListing(r.data);
      })
      .catch(() => {
        if (active) setMyListing(null);
      });
    return () => {
      active = false;
    };
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate("/");
  };

  const navClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 ${
      isActive ? "text-emerald-400" : "text-slate-400 hover:text-slate-100"
    }`;

  const mobileNavClass = ({ isActive }) =>
    `shrink-0 font-mono2 text-[11px] uppercase tracking-[0.14em] transition-colors duration-200 ${
      isActive ? "text-emerald-400" : "text-slate-500 hover:text-slate-100"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-16 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" data-testid="header-logo" aria-label="HighRank.space home" className="shrink-0">
            <span className="md:hidden"><Logo small /></span>
            <span className="hidden md:inline-flex"><Logo /></span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/top-spot" data-testid="nav-top-spot-link" className={navClass}>
              Top Spot
            </NavLink>
            <NavLink to="/leaderboard" data-testid="nav-leaderboard-link" className={navClass}>
              Leaderboard
            </NavLink>
            <NavLink to="/discover" data-testid="nav-discover-link" className={navClass}>
              Discover
            </NavLink>
            {myListing && (
              <NavLink to={`/company/${myListing.slug}`} data-testid="nav-my-listing-link" className={navClass}>
                My Listing
              </NavLink>
            )}
            {!user && (
              <NavLink to="/add-business" data-testid="nav-add-business-link" className={navClass}>
                List your business
              </NavLink>
            )}
            {user && user.role === "admin" && (
              <NavLink to="/admin" data-testid="nav-admin-link" className={navClass}>
                Admin
              </NavLink>
            )}
            {user && user.role === "admin" && (
              <NavLink to="/discover-admin" data-testid="nav-discover-admin-link" className={navClass}>
                Discover Admin
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              data-testid="theme-toggle-button"
              onClick={toggle}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/40 transition-all duration-200"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {user ? (
              <div ref={userMenuRef} className="relative">
                <button
                  data-testid="header-user-menu-button"
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="hidden sm:flex items-center gap-1.5 font-mono2 text-xs text-slate-400 border border-white/10 rounded-full px-3 py-1.5 hover:border-white/20 hover:text-slate-200 transition-all duration-200"
                >
                  <span data-testid="header-user-name">{user.name}</span>
                  <ChevronDown size={12} className={`transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {/* Mobile: just logout button */}
                <button
                  data-testid="nav-logout-button-mobile"
                  onClick={handleLogout}
                  className="sm:hidden flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-400 transition-colors duration-200"
                >
                  <LogOut size={15} />
                </button>
                {userMenuOpen && (
                  <div
                    data-testid="user-dropdown-menu"
                    className="absolute right-0 top-full mt-2 w-44 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1A1A18] shadow-lg shadow-gray-200/80 dark:shadow-black/40 overflow-hidden z-50"
                  >
                    <Link
                      to="/change-password"
                      data-testid="user-menu-change-password"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                    >
                      <KeyRound size={14} className="text-slate-400 dark:text-slate-500" />
                      Change password
                    </Link>
                    <div className="border-t border-gray-100 dark:border-white/5" />
                    <button
                      data-testid="nav-logout-button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2.5 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    >
                      <LogOut size={14} className="text-slate-400 dark:text-slate-500" />
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                data-testid="nav-signin-link"
                className="whitespace-nowrap text-sm font-medium text-slate-300 hover:text-emerald-400 transition-colors duration-200"
              >
                Sign in
              </Link>
            )}
            {!user && (
              <Link
                to="/add-business"
                data-testid="nav-add-business-button"
                className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold rounded-full px-4 py-2 transition-all duration-200 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]"
              >
                Get listed
              </Link>
            )}
          </div>
        </div>

        <nav
          data-testid="mobile-top-nav"
          className="md:hidden h-11 flex items-center gap-3 overflow-x-auto whitespace-nowrap border-t border-white/5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <NavLink to="/top-spot" data-testid="mobile-nav-top-spot-link" className={mobileNavClass}>
            Top Spot
          </NavLink>
          <NavLink to="/leaderboard" data-testid="mobile-nav-leaderboard-link" className={mobileNavClass}>
            Leaderboard
          </NavLink>
          <NavLink to="/discover" data-testid="mobile-nav-discover-link" className={mobileNavClass}>
            Discover
          </NavLink>
          {myListing && (
            <NavLink to={`/company/${myListing.slug}`} data-testid="mobile-nav-my-listing-link" className={mobileNavClass}>
              My Listing
            </NavLink>
          )}
          {!user && (
            <NavLink to="/add-business" data-testid="mobile-nav-add-business-link" className={mobileNavClass}>
              List your business
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/admin" data-testid="mobile-nav-admin-link" className={mobileNavClass}>
              Admin
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/discover-admin" data-testid="mobile-nav-discover-admin-link" className={mobileNavClass}>
              Disc. Admin
            </NavLink>
          )}
        </nav>
      </div>
    </header>
  );
}
